import type { ConfigurableProperty, DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import { useSavedFilters } from 'api/savedFilters/savedFilters.api'
import type { AppliedSettingSeries } from 'helpers/appliedSettingValues.helper/appliedSettingValues.types'
import { useApplySavedFilter } from 'hooks/filterHooks/useApplySavedFilter'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { countSavedFilterSeries, describeSavedFilter } from './SavedFilterSummary.helper'
import { useSavedFilterSelections } from './useSavedFilterSelections'

type AppliedSettingValuesEmptyStateProps = {
  availableFiltersSubset: string[][]
  allProperties: ConfigurableProperty[]
  priorityLevels: number[]
  catalogue: AppliedSettingSeries[]
  translations: DigitalTwinTranslations
}

export function AppliedSettingValuesEmptyState({
  availableFiltersSubset,
  allProperties,
  priorityLevels,
  catalogue,
  translations,
}: AppliedSettingValuesEmptyStateProps): ReactNode {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const { data: savedFilters = [] } = useSavedFilters(systemId?.toString())
  const applySavedFilter = useApplySavedFilter()
  const urlFiltersById = useSavedFilterSelections(savedFilters)

  return (
    <div className="flex flex-col items-center gap-6 py-6">
      <div className="flex max-w-xl flex-col items-center gap-2 text-center">
        <NewIcon icon="fal fa-filter" color="grey" size="large" ariaLabel="" />
        <h3 className="m-0 text-xl font-bold text-grey-8">{t('No filter selected')}</h3>
        <p className="m-0 text-grey-6">
          {t('Choose units or properties in the filter above to see their applied setting values')}
          {savedFilters.length > 0 && ` — ${t('Or use one of your saved filters').toLowerCase()}:`}
        </p>
      </div>
      {savedFilters.length > 0 && (
        <ul className="m-0 grid w-full list-none grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4 p-0">
          {[...savedFilters]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((filter) => {
              const urlFilters = urlFiltersById.get(filter.id)
              const filterParts = urlFilters ? describeSavedFilter(urlFilters, translations) : []
              const seriesCount = urlFilters
                ? countSavedFilterSeries(urlFilters, {
                    availableFiltersSubset,
                    allProperties,
                    priorityLevels,
                    catalogue,
                  })
                : undefined

              return (
                <li key={filter.id} className="flex">
                  <button
                    type="button"
                    aria-label={t('Use saved filter {{name}}', { name: filter.name })}
                    onClick={() => applySavedFilter(filter)}
                    className="group flex w-full cursor-pointer flex-col justify-between gap-4 rounded-md border border-gray-300 bg-white p-4 text-left transition-colors hover:border-primary-5 hover:bg-grey-1 focus-visible:outline-2 focus-visible:outline-primary-5"
                  >
                    <span className="flex flex-col gap-2">
                      <span className="flex items-start gap-2 font-bold text-grey-8">
                        <span className="shrink-0">
                          <NewIcon icon="fal fa-bookmark" ariaLabel="" />
                        </span>
                        <span className="min-w-0 break-words">{filter.name}</span>
                      </span>
                      {filterParts.map((part) => (
                        <span key={part} className="text-sm text-grey-7">
                          {part}
                        </span>
                      ))}
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm text-grey-6">
                        {seriesCount !== undefined && t('{{count}} series', { count: seriesCount })}
                      </span>
                      <span className="inline-flex items-center gap-1 text-primary-5">
                        <span className="group-hover:underline">{t('Use')}</span>
                        <NewIcon icon="fal fa-arrow-right" size="small" ariaLabel="" />
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
        </ul>
      )}
    </div>
  )
}
